import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import _ from 'lodash'
import { X, User, Building2, Edit2, Check, X as XIcon } from 'lucide-react'
import {
  AllTransactionsType,
  GetGlobalCompaniesQuery,
  GetNewWatchlistsQuery,
  GlobalCompanySearchType,
  WatchlistObject,
  WatchlistEntityType,
  useGetGlobalCompaniesQuery,
  useGetNewWatchlistsQuery,
  RemoveWatchlistMutation,
  RemoveWatchlistMutationVariables,
  useRemoveWatchlistMutation,
  EditWatchlistNameMutation,
  EditWatchlistNameMutationVariables,
  useEditWatchlistNameMutation,
} from '../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../queries/client'
import { useWatchlistDetails } from './hooks/useWatchlistDetails'
import { AddWatchlist } from './Components/AddWatchlist'
import { EmptyWatchlistCard } from './Components/EmptyWatchlistCard'
import { WatchlistAGTable } from './Components/WatchlistAGTable'
import { WatchlistHeaderCard } from './Components/WatchlistHeaderCard'
import { WatchlistTickerCard } from './Components/WatchlistTickerCard'
import { GlobalSearchBar } from './Components/GlobalSearchBar'
import { KTSVG, toAbsoluteUrl } from '../../../_metronic/helpers'
import { DeleteConfirmPopup } from '../../../utils/DeleteConfirmPopup'
import Toast, { ToastType } from '../../../utils/toast'
import Ticker from 'react-ticker'
import PageVisibility from 'react-page-visibility'
import { globalCompaniesList } from '../../../utils/GlobalComapnies'

function WatchlistPageWrapper() {
  const [addWatchlist, setAddWatchlist] = useState<boolean>(false)
  const [searchOptions, setSearchOptions] = useState<boolean>(false)
  const [watchlists, setWatchlists] = useState<WatchlistObject[]>([])
  const [allCompanies, setAllCompanies] = useState<GlobalCompanySearchType[]>([])
  const [filteredCompanies, setFilteredCompanies] = useState<GlobalCompanySearchType[]>([])
  const [currentWatchlistId, setCurrentWatchlistId] = useState<number | null>(null)
  const [watchlistTicker, setWatchlistTicker] = useState<any[]>([])
  const [tickerKey, setTickerKey] = useState(0)

  const [isPaused, setIsPaused] = useState(false)
  const [pendingWatchlistName, setPendingWatchlistName] = useState<string | null>(null)
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false)
  const [watchlistToDelete, setWatchlistToDelete] = useState<{ id: number; name: string } | null>(
    null
  )
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

  const searchInputRef = useRef<HTMLInputElement>(null)

  const location = useLocation()
  let existingWatchlist: any = location.state

  // Load basic watchlist info (without companies)
  const { data: watchlistsData, isLoading: isLoadingWatchlists } = useGetNewWatchlistsQuery<
    GetNewWatchlistsQuery,
    Error
  >(graphqlRequestClient, {}, {})

  // Load detailed watchlist data only when a watchlist is selected
  const {
    watchlistDetails: currentWatchlist,
    isLoading: isLoadingWatchlistDetails,
    isFetching: isFetchingWatchlistDetails,
  } = useWatchlistDetails(currentWatchlistId)

  // Load global companies
  const { data: globalCompanies } = useGetGlobalCompaniesQuery<GetGlobalCompaniesQuery, Error>(
    graphqlRequestClient,
    {},
    {}
  )

  // Delete watchlist mutation
  const mutateDeleteWatchlist = useRemoveWatchlistMutation<RemoveWatchlistMutation, Error>(
    graphqlRequestClient,
    {},
    {}
  )

  // Edit watchlist name mutation
  const mutateEditWatchlistName = useEditWatchlistNameMutation<EditWatchlistNameMutation, Error>(
    graphqlRequestClient,
    {},
    {}
  )

  const [isPageVisible, setIsPageVisible] = useState(true)

  const clearSearchResults = (createdWatchlistName?: string) => {
    setFilteredCompanies([])
    if (searchInputRef.current) {
      searchInputRef.current.value = ''
    }

    // Set the pending watchlist name to navigate to it after data is refetched
    if (createdWatchlistName) {
      setPendingWatchlistName(createdWatchlistName)
    }
  }

  const restartTicker = () => {
    setTickerKey((prevKey) => prevKey + 1)
  }

  const handleVisibilityChange = (isVisible: boolean) => {
    setIsPageVisible(isVisible)
  }

  const handleDeleteClick = useCallback((e: React.MouseEvent, watchlist: WatchlistObject) => {
    e.stopPropagation() // Prevent tab click
    setWatchlistToDelete({ id: watchlist.id!, name: watchlist.name! })
    setShowDeletePopup(true)
  }, [])

  const handleDeleteWatchlist = useCallback(() => {
    if (!watchlistToDelete) return

    setDeleteLoading(true)
    mutateDeleteWatchlist.mutate(
      {
        input: watchlistToDelete.id,
      },
      {
        onSuccess: (
          data: RemoveWatchlistMutation,
          _variables: RemoveWatchlistMutationVariables,
          context: unknown
        ) => {
          queryClient.invalidateQueries(useGetNewWatchlistsQuery.getKey())

          // If the deleted watchlist was the current one, switch to another
          if (currentWatchlistId === watchlistToDelete.id) {
            const remainingWatchlists = watchlists.filter((w) => w.id !== watchlistToDelete.id)
            setCurrentWatchlistId(
              remainingWatchlists.length > 0 ? remainingWatchlists[0].id! : null
            )
          }

          Toast('Watchlist Deleted Successfully!', ToastType.success)
          setDeleteLoading(false)
          setShowDeletePopup(false)
          setWatchlistToDelete(null) // This will trigger popup remount via key prop
        },
        onError: (error: any) => {
          setDeleteLoading(false)
          Toast(error.response?.errors?.[0]?.message || 'Error deleting watchlist', ToastType.error)
        },
      }
    )
  }, [watchlistToDelete, mutateDeleteWatchlist, currentWatchlistId, watchlists])

  // Update watchlists when data loads
  useEffect(() => {
    if (watchlistsData?.newWatchlists) {
      setWatchlists(_.cloneDeep(watchlistsData.newWatchlists) as WatchlistObject[])

      // Check if we have a pending watchlist to navigate to
      if (pendingWatchlistName) {
        const newWatchlist = watchlistsData.newWatchlists.find(
          (w: any) => w.name === pendingWatchlistName
        )
        if (newWatchlist?.id) {
          setCurrentWatchlistId(newWatchlist.id)
          setPendingWatchlistName(null) // Clear the pending state
        }
      }
    }
  }, [watchlistsData, pendingWatchlistName])

  // Set initial current watchlist
  useEffect(() => {
    if (existingWatchlist === null && watchlists.length > 0 && !currentWatchlistId) {
      setCurrentWatchlistId(watchlists[0]?.id || null)
    }

    if (watchlists.length > 0 && existingWatchlist !== null) {
      const index = watchlists.findIndex(
        (watchlist) => watchlist.name === existingWatchlist.watchlistName
      )
      if (index > -1) {
        setCurrentWatchlistId(watchlists[index]?.id || null)
      } else {
        setCurrentWatchlistId(watchlists[0]?.id || null)
      }
    }

    if (watchlists.length === 0) {
      setCurrentWatchlistId(null)
    }
  }, [existingWatchlist, watchlists, currentWatchlistId])

  // Memoize search function to avoid recreating it on every render
  const getSearchedCompanies = useMemo(() => {
    return _.debounce((search: string) => {
      if (!search) {
        setFilteredCompanies([])
        return
      }

      const searchLower = search.toLowerCase()

      const startsWithCompanies = allCompanies.filter((company) => {
        const name = company.name?.toLowerCase() || ''
        const dba = company.dba?.toLowerCase() || ''
        return name.startsWith(searchLower) || dba.startsWith(searchLower)
      })

      const includesCompanies = allCompanies.filter((company) => {
        const name = company.name?.toLowerCase() || ''
        const dba = company.dba?.toLowerCase() || ''
        return (
          (name.includes(searchLower) && !name.startsWith(searchLower)) ||
          (dba.includes(searchLower) && !dba.startsWith(searchLower))
        )
      })

      const filteredResult = [...startsWithCompanies, ...includesCompanies]
      setFilteredCompanies(filteredResult)
    }, 300) // 300ms debounce to reduce API calls
  }, [allCompanies])

  // Memoize ticker data to avoid unnecessary recalculations
  const memoizedTickerData = useMemo(() => {
    if (currentWatchlist?.companies && currentWatchlist.companies?.length > 0) {
      const sortedWatchlist = [...currentWatchlist.companies].sort((a: any, b: any) => {
        const starredCompanies = currentWatchlist?.starredCompanies ?? []
        return (
          +starredCompanies.includes(b.companyData.hissaCompanyId) -
          +starredCompanies.includes(a.companyData.hissaCompanyId)
        )
      })

      const companyDataList: any[] = []
      sortedWatchlist.forEach((company: any) => {
        const companyData = {
          name: company.companyData.name,
          valuation: company.companyData.valuation,
          year: formatDate(company.companyData.latestRaiseDate) || '',
          logo: company.companyData.logo || '',
          companyId: company.companyData.hissaCompanyId,
        }
        companyDataList.push(companyData)
      })

      return companyDataList.length >= 10 ? companyDataList : globalCompaniesList
    }

    return globalCompaniesList
  }, [currentWatchlist?.companies, currentWatchlist?.starredCompanies])

  // Memoize tab click handler to prevent unnecessary re-renders
  const handleTabClick = useCallback((watchlistId: number | null) => {
    setCurrentWatchlistId(watchlistId)
  }, [])

  // Memoize watchlist tabs rendering
  const watchlistTabs = useMemo(() => {
    return watchlists.map((watchlist) => (
      <span
        className={`border me-4 p-2 rounded rounded-pill bg-white border-1 ${
          currentWatchlistId === watchlist.id ? 'border-primary' : 'border-gray-400'
        } position-relative`}
        style={{ cursor: 'pointer' }}
        key={`watchlist-${watchlist.id}`}
        onClick={() => handleTabClick(watchlist.id || null)}
      >
        <div
          className={`badge text-wrap fs-7 m-3 pe-5 ${
            currentWatchlistId === watchlist.id ? 'text-primary' : 'text-gray-800'
          }`}
        >
          {watchlist.type === WatchlistEntityType.Investor && (
            <User
              size={12}
              className='me-1'
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
          )}
          {watchlist.type === WatchlistEntityType.Company && (
            <Building2
              size={12}
              className='me-1'
              style={{ display: 'inline-block', verticalAlign: 'middle' }}
            />
          )}
          {watchlist.name}
        </div>
        {
          <button
            className='btn btn-sm p-0 position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={(e) => handleDeleteClick(e, watchlist)}
            title='Delete watchlist'
            aria-label='Delete watchlist'
          >
            <X size={16} className='text-primary' />
          </button>
        }
      </span>
    ))
  }, [watchlists, currentWatchlistId, handleTabClick, handleDeleteClick])

  // Optimize loading state to prevent flickering
  // Only show initial loading for the entire page, not for tab switches
  const isInitialPageLoading = isLoadingWatchlists && watchlists.length === 0

  useEffect(() => {
    if (globalCompanies?.globalCompaniesSearch) {
      setAllCompanies(
        _.cloneDeep(globalCompanies.globalCompaniesSearch) as GlobalCompanySearchType[]
      )
    }
  }, [globalCompanies])

  useEffect(() => {
    setWatchlistTicker(memoizedTickerData)
    restartTicker()
  }, [memoizedTickerData])

  if (isInitialPageLoading)
    return (
      <div className='w-100 my-20 d-flex justify-content-center align-items-center'>
        <span className='spinner-grow'></span>
      </div>
    )

  function formatDate(dateString: string): string {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    }
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short' }
    return date.toLocaleDateString('en-US', options)
  }

  return (
    <>
      <div className='d-flex justify-content-start align-items-center my-4'>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-baseline border-4 border-start border-primary ps-4'>
            <h2 className='mb-0 fw-bold my-1'>Watchlist</h2>
          </div>
        </div>
      </div>

      {/* Tickers */}
      {allCompanies.length > 0 && watchlistTicker.length > 0 && (
        <div className='row mb-8'>
          <div className='col-12'>
            <div
              className='card bg-transparent overflow-auto'
              style={{ overflowX: 'auto', scrollbarWidth: 'none' }}
            >
              <PageVisibility onChange={handleVisibilityChange}>
                {isPageVisible && (
                  <Ticker key={tickerKey} speed={5} move={!isPaused}>
                    {({ index }) => (
                      <div
                        key={index}
                        className='mt-4 card-body p-0 row flex-nowrap mx-0 mb-1'
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                      >
                        {watchlistTicker.map((company, i) => (
                          <span
                            key={`ticker-${company.companyId}-${i}`}
                            style={{
                              display: index % watchlistTicker.length === i ? 'block' : 'none',
                            }}
                          >
                            <WatchlistTickerCard
                              companyId={company.companyId}
                              companyName={company?.name!}
                              valuation={company?.valuation!}
                              index={i}
                              logo={company.logo}
                              year={company.year}
                            />
                          </span>
                        ))}
                      </div>
                    )}
                  </Ticker>
                )}
              </PageVisibility>
            </div>
          </div>
        </div>
      )}
      {/* Global Search Bar*/}
      <div className='row mb-8'>
        <div className='col-12 d-flex justify-content-center'>
          <span
            className={`d-flex flex-column items-center bg-white w-full ${
              filteredCompanies.length > 0
                ? ' rounded position-absolute shadow-sm'
                : 'rounded rounded-pill'
            }`}
            style={{
              boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
              border: '1',
              borderColor: '#F1F1F2',
              zIndex: 6,
            }}
          >
            <span className='d-flex flex-row'>
              <KTSVG
                path={'media/icons/duotune/general/gen021.svg'}
                className='ps-4 svg-icon-2x text-start fs-9 pt-3'
                svgClassName='text-gray-700'
              />
              <input
                ref={searchInputRef}
                type='search'
                placeholder='Search for Companies and Investors'
                className='form-control w-350px w-lg-650px border-0 rounded rounded-pill'
                onClick={() => {
                  setSearchOptions(!searchOptions)
                }}
                onChange={(e) => {
                  getSearchedCompanies(e.target.value)
                }}
              />
            </span>
            <GlobalSearchBar
              filteredCompanies={filteredCompanies}
              currentWatchlist={currentWatchlist!}
              onSearchClose={clearSearchResults}
            />
          </span>
        </div>
      </div>
      {/* Watchlist Tabs */}
      <div className='row mb-8'>
        <div className={`col-md-12 ${filteredCompanies.length > 0 ? 'pt-13' : ''}`}>
          {watchlistTabs}
          {watchlists.length > 0 && (
            <span
              className={`m-2 p-2 px-0 border-bottom border-hover-primary border-1 ${
                addWatchlist ? 'border-primary' : ''
              }`}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setAddWatchlist(!addWatchlist)
              }}
            >
              <div
                className={`badge text-wrap fs-7 m-3 ${
                  addWatchlist ? ' text-primary' : ' text-gray-800'
                }`}
              >
                {'+ New Watchlist'}
              </div>
            </span>
          )}
          {addWatchlist && (
            <AddWatchlist
              showModal={addWatchlist}
              handleClose={() => setAddWatchlist(false)}
              isEdit={false}
              onWatchlistCreated={clearSearchResults}
            />
          )}
        </div>
      </div>
      {/* Watchlist Starred Cards */}
      {currentWatchlist?.starredCompanies &&
        currentWatchlist?.starredCompanies?.length > 0 &&
        !isFetchingWatchlistDetails && (
          <div className='row mb-8'>
            <div className='col-12'>
              {currentWatchlist?.starredCompanies &&
                currentWatchlist?.starredCompanies.length > 0 && (
                  <h4 className='pb-2 fw-bold'>{`Starred Companies in ${
                    currentWatchlist?.name || 'Watchlist'
                  }`}</h4>
                )}
              <div
                className='card bg-transparent overflow-auto'
                style={{ overflowY: 'hidden', overflowX: 'auto', scrollbarWidth: 'none' }}
              >
                <div className='card-body p-0 row flex-nowrap mx-0 mb-1 gap-9'>
                  {currentWatchlist?.companies?.map(
                    (company, index) =>
                      currentWatchlist.starredCompanies?.includes(
                        company?.companyData.hissaCompanyId!
                      ) && (
                        <WatchlistHeaderCard
                          key={`starred-${company?.companyData.hissaCompanyId}`}
                          companyName={company?.companyData.name || ''}
                          className=''
                          initIndex={index + 1}
                          dba={company?.companyData.dba || ''}
                          logo={company?.companyData.logo || ''}
                          valuation={company?.companyData.valuation || 0}
                          sharePrice={company?.companyData.sharePrice || 0}
                        />
                      )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      {/* Watchlist AG Table */}
      <div className='row mb-9'>
        {/* Show loading when fetching data for any tab switch */}
        {isFetchingWatchlistDetails && currentWatchlistId && (
          <div className='w-100 my-20 d-flex justify-content-center align-items-center'>
            <span className='spinner-grow'></span>
          </div>
        )}
        {/* Show table when we have data and not fetching */}
        {currentWatchlist?.companies &&
          currentWatchlist?.companies.length > 0 &&
          !isFetchingWatchlistDetails && (
            <div className='col-12'>
              <WatchlistAGTable
                watchlistCompanies={
                  currentWatchlist?.companies?.sort((a: any, b: any) =>
                    a.companyData.name.localeCompare(b.companyData.name)
                  ) as AllTransactionsType[]
                }
                watchlistId={currentWatchlist?.id}
                watchlistName={currentWatchlist.name!}
                starredCompanies={currentWatchlist.starredCompanies as string[]}
                watchlistType={currentWatchlist.type || undefined}
              />
            </div>
          )}
        {/* Show empty state when watchlist has no companies and not fetching */}
        {currentWatchlist?.companies &&
          currentWatchlist?.companies.length === 0 &&
          !isFetchingWatchlistDetails && (
            <div className='col-12'>
              <EmptyWatchlistCard
                watchlistId={currentWatchlist?.id}
                watchlistName={currentWatchlist?.name!}
                watchlistType={currentWatchlist.type || undefined}
              />
            </div>
          )}
        {/* Show no watchlist found state */}
        {!isInitialPageLoading && !currentWatchlistId && (
          <div>
            <div
              className='card p-0 h-400px'
              style={{
                boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
                border: '1',
                borderColor: '#F1F1F2',
              }}
            >
              <div className='card-body'>
                <div className='row justify-content-center  text-center m-4'>
                  <img
                    alt='no items'
                    src={toAbsoluteUrl('/media/custom-images/noWatchlist.svg')}
                    className='h-200px mb-6 mw-250 justify-content-center'
                  />
                  <h1>No Watchlist Found</h1>
                  <span className='fw-semibold fs-6'>Click below to create a new watchlist</span>
                  <div className='col justify-content-center pt-4'>
                    <button
                      className='btn btn-primary mt-4 mx-8'
                      data-bs-toggle='modal'
                      data-bs-target='#kt_modal_1'
                      onClick={() => {
                        setAddWatchlist(!addWatchlist)
                      }}
                    >
                      Add Watchlist
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Watchlist Confirmation Popup */}
      <DeleteConfirmPopup
        key={watchlistToDelete?.id || 'no-watchlist'} // Force remount on watchlist change to reset input
        showModel={showDeletePopup}
        setShowModel={setShowDeletePopup}
        primaryAction={handleDeleteWatchlist}
        secondaryAction={() => {
          setShowDeletePopup(false)
          setWatchlistToDelete(null)
        }}
        loading={deleteLoading}
        primaryButtonText='Delete Watchlist'
        secondaryButtonText='Cancel'
        title='Delete Watchlist'
        description='This action cannot be undone. Please type the watchlist name to confirm.'
        confirmText={`Type "${watchlistToDelete?.name}" to confirm deletion:`}
        currentValue={watchlistToDelete?.name}
      />
    </>
  )
}

export default WatchlistPageWrapper
